/**
 * The demo world — ONE consistent set of fake guests that powers every
 * demo-enabled page: the reservations list, the contract detail, the
 * Analytics hub and the journey responses. A guest you click in the list
 * opens as a contract; the same names appear in the dashboard drills.
 *
 * Everything is generated relative to "today", so the demo always looks
 * current. UI only — nothing here ever reaches the backend.
 */

const GUESTS = [
  ['Emma', 'Johansson'], ['Liam', 'Bergmann'], ['Sofia', 'Rossi'], ['Noah', 'De Vries'],
  ['Mia', 'Kowalski'], ['Lucas', 'Fernandez'], ['Amelie', 'Laurent'], ['David', 'Cohen'],
  ['Yuki', 'Tanaka'], ['Olivia', 'Smith'], ['Jonas', 'Weber'], ['Chiara', 'Bianchi'],
  ['Felix', 'Novak'], ['Sara', 'Lindqvist'], ['Daniel', 'Petrov'], ['Ines', 'Silva'],
  ['Tom', 'Bakker'], ['Hannah', 'Gruber'], ['Marco', 'Ricci'], ['Lea', 'Steiner'],
  ['James', 'Wilson'], ['Nina', 'Horvath'], ['Pierre', 'Dubois'], ['Anna', 'Virtanen'],
]

/* shaped like real RealEstate rows — the reservations table renders address/house/apt/city from these */
const PROPERTIES = [
  {id: 'demo-prop-1', name: 'Grand Boulevard Suites', address: 'Ringstraße', house: 12, apt: '3B', city: 'Vienna', country: 'Austria', attachments: null},
  {id: 'demo-prop-2', name: 'Palais Central', address: 'Opernring', house: 5, apt: 'PH7', city: 'Vienna', country: 'Austria', attachments: null},
  {id: 'demo-prop-3', name: 'River Lofts', address: 'Donaukanal', house: 8, apt: '12', city: 'Vienna', country: 'Austria', attachments: null},
  {id: 'demo-prop-4', name: 'Old Town Residence', address: 'Graben', house: 21, apt: '2A', city: 'Vienna', country: 'Austria', attachments: null},
  {id: 'demo-prop-5', name: 'Garden Apartments', address: 'Prater Allee', house: 3, apt: '5C', city: 'Vienna', country: 'Austria', attachments: null},
  {id: 'demo-prop-6', name: 'Sky Terrace', address: 'Mariahilfer Straße', house: 88, apt: '9A', city: 'Vienna', country: 'Austria', attachments: null},
]
function fullAddress(property) { return `${property.name} · ${property.address} ${property.house}/${property.apt}, ${property.city}` }

const COUNTRIES = ['Sweden', 'Germany', 'Italy', 'Netherlands', 'Poland', 'Spain', 'France', 'Israel', 'Japan', 'United Kingdom', 'Austria', 'Switzerland']

const PLATFORMS = ['Airbnb', 'Booking', 'Treasure Home', 'Expedia', 'Airbnb', 'Booking', 'Treasure Home', 'Vrbo']

const PRODUCTS = [
  {name: 'Late checkout', price: 45}, {name: 'Early check-in', price: 35},
  {name: 'Breakfast box', price: 18}, {name: 'Parking spot', price: 15},
  {name: 'Airport transfer', price: 60}, {name: 'Bottle of wine', price: 29},
  {name: 'Baby bed', price: 12},
]

function fmt(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}
function fmtTime(date) { return `${fmt(date)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}` }
function addDays(base, days) { const d = new Date(base.getTime()); d.setDate(d.getDate() + days); return d }
function code(seed) {
  const alphabet = 'ABCDEFGHJKMNPQRSTWXYZ23456789'
  let out = 'HM'
  for (let i = 0; i < 8; i++) out += alphabet[(seed * 7 + i * 13) % alphabet.length]
  return out
}

/** deterministic pseudo-random so the demo looks stable during one presentation */
function rnd(seed) { const x = Math.sin(seed * 999) * 10000; return x - Math.floor(x) }

let cache = null

export function demoContracts() {
  if (cache) return cache
  const today = new Date()
  const contracts = GUESTS.map(([firstname, lastname], index) => {
    const property = PROPERTIES[index % PROPERTIES.length]
    const platform = PLATFORMS[index % PLATFORMS.length]
    const checkInOffset = Math.round(rnd(index + 1) * 24) - 9      // -9 .. +15 days
    const nights = 2 + Math.round(rnd(index + 2) * 5)              // 2 .. 7
    const moveDate = addDays(today, checkInOffset)
    const endDate = addDays(moveDate, nights)
    const perNight = 95 + Math.round(rnd(index + 3) * 160)
    const total = perNight * nights
    const cancelled = index % 11 === 5
    const verified = !cancelled && rnd(index + 4) > 0.2
    const signed = platform === 'Treasure Home' && rnd(index + 5) > 0.4
    const guests = 1 + Math.round(rnd(index + 6) * 3)

    return {
      id: `demo-${index + 1}`,
      status: cancelled ? 'CANCELED' : 'ACTIVE',
      active: true,
      clientId: 900001 + index,
      client: {
        id: 900001 + index, firstname, lastname,
        email: `${firstname.toLowerCase()}.${lastname.toLowerCase().replace(/\s/g, '')}@demo.guest`,
        phone: `+43 660 ${String(1000000 + index * 4711).slice(0, 7)}`,
        address: {country: COUNTRIES[index % COUNTRIES.length], city: '', street: ''},
      },
      clientName: `${firstname} ${lastname}`,
      propertyId: property.id,
      propertyAddress: fullAddress(property),
      buildingId: 'demo-building-1',
      creationTime: fmtTime(addDays(moveDate, -Math.round(rnd(index + 7) * 30) - 3)),
      moveDate: fmt(moveDate),
      endDate: fmt(endDate),
      finalMoveDate: fmt(moveDate),
      price: total,
      initialPrice: total,
      deposit: 200,
      depositPaid: rnd(index + 8) > 0.5,
      depositRefunded: false,
      currency: 'EUR',
      numberOfGuests: guests,
      platformReservationId: platform === 'Airbnb' ? code(index + 1) : (platform === 'Booking' ? String(4100000000 + index * 987654) : ''),
      hostawayReservationId: String(38000000 + index * 1337),
      signedOffer: signed,
      skipJourney: false,
      paid: platform !== 'Treasure Home',
      payedRentFees: platform !== 'Treasure Home',
      payedHandlingFees: true,
      guestNote: index % 6 === 2 ? 'Arriving late, around 22:00 — please leave instructions.' : null,
      alerts: [],
      statusHistories: [{status: 'NEW'}, {status: cancelled ? 'CANCELED' : 'ACTIVE'}],
      clientPayments: verified
        ? [{id: `demo-pay-${index}`, contractId: `demo-${index + 1}`, amount: 0.99, currency: 'EUR', reason: 'Authorization fee', paid: true, type: 'CARD', digits: String(4000 + index), brand: index % 2 ? 'visa' : 'mastercard', paymentDate: fmtTime(addDays(moveDate, -2))}]
        : [],
      contractOrders: [],
      files: [],
      bnbStyleRent: {
        active: true,
        platform,
        start: fmt(moveDate),
        end: fmt(endDate),
        totalNights: nights,
        totalPayment: total,
        commissionAmount: platform === 'Treasure Home' ? 0 : Math.round(total * 0.14),
        paymentProcessingFee: 0,
        cleaningFee: 45,
        currency: 'EUR',
        internalIncome: platform === 'Treasure Home',
        oneTimePayment: true,
        longPeriod: false,
        keyCode: `#${2400 + index}`,
        wifiUsername: 'TreasureGuest',
        wifiPassword: 'welcome24',
      },
      _demo: {verified, journeyDone: verified && rnd(index + 9) > 0.25},
    }
  })
  cache = contracts
  return contracts
}

// ---------------------------------------------------------------- lists

/** Basic filter support so the reservations filter bar feels REAL in a demo. */
function matchesFilter(contract, filter) {
  if (!filter) return true
  const like = filter.like || {}
  const eq = filter.eq || {}
  const client = eq.client || {}
  const inFilter = filter.in || {}
  const contains = (value, term) => String(value || '').toLowerCase().includes(String(term).toLowerCase())

  if (like.id && !contains(contract.id, like.id)) return false
  if (like.platformReservationId && !contains(contract.platformReservationId, like.platformReservationId)) return false
  if (Array.isArray(inFilter.status) && inFilter.status.length && !inFilter.status.includes(contract.status)) return false
  if (eq.bnbStyleRent && eq.bnbStyleRent.platform && contract.bnbStyleRent.platform !== eq.bnbStyleRent.platform) return false
  if (eq.propertyId && contract.propertyId !== eq.propertyId) return false
  if (client.firstname && !contains(contract.client.firstname, client.firstname)) return false
  if (client.lastname && !contains(contract.client.lastname, client.lastname)) return false
  if (client.email && !contains(contract.client.email, client.email)) return false
  if (filter.gte && filter.gte.moveDate && contract.moveDate < filter.gte.moveDate) return false
  if (filter.lte && filter.lte.moveDate && contract.moveDate > filter.lte.moveDate) return false
  return true
}

export function contractsPayload({page = 1, amount = 25, filter = null} = {}) {
  const all = demoContracts().filter(contract => matchesFilter(contract, filter))
  const from = (page - 1) * amount
  return {
    contracts: all.slice(from, from + amount),
    // the list page maps these by id for the property column (image/address/city)
    realEstates: PROPERTIES,
    Page: page, Amount: amount, TotalRows: all.length, Total: all.length,
    sortColumn: 'creationTime', sortOrder: 'desc',
  }
}

export function contractById(id) {
  const contract = demoContracts().find(c => c.id === id)
  return contract ? {contract} : null
}

// ---------------------------------------------------------------- analytics

function toStatsRow(contract) {
  return {
    contractId: contract.id, clientId: contract.clientId, clientName: contract.clientName,
    propertyId: contract.propertyId, propertyAddress: contract.propertyAddress,
    moveDate: contract.moveDate, endDate: contract.endDate,
    platform: contract.bnbStyleRent.platform, amount: contract.bnbStyleRent.totalPayment,
    signedOffer: contract.signedOffer,
  }
}

function inRange(dateString, start, end) { return dateString >= start && dateString <= end }

export function analyticsPayload(request = {}) {
  const start = request.start || fmt(addDays(new Date(), -30))
  const end = request.end || fmt(addDays(new Date(), 30))
  const all = demoContracts()
  const active = all.filter(c => c.status === 'ACTIVE')

  const checkin = active.filter(c => inRange(c.moveDate, start, end)).map(toStatsRow)
  const checkout = active.filter(c => inRange(c.endDate, start, end)).map(toStatsRow)
  const staying = active.filter(c => c.moveDate < start && c.endDate > end).map(toStatsRow)
  const cancelled = all.filter(c => c.status === 'CANCELED' && inRange(c.moveDate, start, end)).map(toStatsRow)
  const newBookings = all.filter(c => inRange(c.creationTime.slice(0, 10), start, end)).map(toStatsRow)

  // upsell orders belong to arriving guests, so every drill stays consistent
  const arrivals = active.filter(c => inRange(c.moveDate, start, end))
  const items = []
  arrivals.forEach((contract, index) => {
    const howMany = index % 3 === 0 ? 2 : (index % 2 === 0 ? 1 : 0)
    for (let i = 0; i < howMany; i++) {
      const product = PRODUCTS[(index + i * 3) % PRODUCTS.length]
      items.push({
        name: product.name, clientName: contract.clientName, contractId: contract.id,
        propertyAddress: contract.propertyAddress, price: product.price, currency: 'EUR',
        status: rnd(index + i + 10) > 0.3 ? 'PAID' : 'REQUESTED',
        requestTime: `${contract.moveDate}T10:3${i}:00`,
      })
    }
  })
  const paid = items.filter(i => i.status === 'PAID')
  const topByName = {}
  items.forEach(item => {
    const top = topByName[item.name] || (topByName[item.name] = {name: item.name, count: 0, revenue: 0, currency: 'EUR'})
    top.count += 1
    top.revenue += item.price
  })

  const verification = arrivals.map(contract => ({
    contractId: contract.id, clientName: contract.clientName, propertyAddress: contract.propertyAddress,
    platform: contract.bnbStyleRent.platform, moveDate: contract.moveDate, endDate: contract.endDate,
    verified: contract._demo.verified, signed: contract.signedOffer, paid: contract.paid,
    journeyStatus: contract._demo.journeyDone ? 'SUBMITTED' : (contract._demo.verified ? 'DRAFT' : null),
    ready: contract._demo.verified && contract._demo.journeyDone,
  }))

  const started = verification.filter(v => v.journeyStatus != null).length
  return {
    Contracts: {checkin, checkout, staying, cancelled, inRange: checkin.length},
    NewBookings: newBookings,
    Journey: {
      started,
      submitted: verification.filter(v => v.journeyStatus === 'SUBMITTED').length,
      upsellRevenue: paid.reduce((sum, i) => sum + i.price, 0),
      upsellPending: items.filter(i => i.status !== 'PAID').reduce((sum, i) => sum + i.price, 0),
      upsellContracts: new Set(items.map(i => i.contractId)).size,
      currency: 'EUR',
      topProducts: Object.values(topByName).sort((a, b) => b.revenue - a.revenue),
      items,
    },
    Verification: verification,
  }
}

export function receptionPayload() {
  const today = new Date()
  const calls = Array.from({length: 18}, (unused, index) => ({
    from: `+43 664 ${String(2000000 + index * 52341).slice(0, 7)}`,
    to: `+43 1 5${220 + index}`,
    status: index % 5 === 3 ? 'no-answer' : 'completed',
    direction: index % 3 === 0 ? 'outbound-api' : 'inbound',
    startTime: fmtTime(addDays(today, -index)) , duration: String(40 + index * 12),
  }))
  const sms = Array.from({length: 12}, (unused, index) => ({
    to: `+43 660 ${String(3000000 + index * 41777).slice(0, 7)}`,
    message: index % 2 ? 'Your check-in is ready — complete it here: https://…' : 'Reminder: check-out tomorrow at 11:00. Late checkout available in the app.',
    sendDate: fmtTime(addDays(today, -index)),
  }))
  const whatsapp = Array.from({length: 14}, (unused, index) => {
    const contract = demoContracts()[index % GUESTS.length]
    return {
      customerName: contract.clientName, contractId: contract.id,
      body: index % 3 === 0 ? 'What is the wifi password?' : (index % 3 === 1 ? 'Can we get a late checkout tomorrow?' : 'Thanks, everything was perfect! 🌟'),
      incoming: index % 4 !== 1, date: fmtTime(addDays(today, -index)),
    }
  })
  return {
    calls: 214, callsAnswered: 192, callsMissed: 22,
    sms: 168, whatsapp: 342, avgResponseSeconds: 41,
    callList: calls, smsList: sms, whatsappList: whatsapp,
  }
}

// ---------------------------------------------------------------- journey

export function journeyResponsesPayload({page = 1, amount = 25} = {}) {
  const submitted = demoContracts().filter(c => c._demo.journeyDone)
  const responses = submitted.map((contract, index) => {
    const products = []
    if (index % 2 === 0) {
      const product = PRODUCTS[index % PRODUCTS.length]
      products.push({guestFeatureId: `demo-feature-${index}`, name: product.name, amount: 1, price: product.price, currency: 'EUR', featureRequestId: `demo-request-${index}`})
    }
    return {
      id: `demo-response-${index + 1}`,
      contractId: contract.id,
      journeyId: 'demo-journey',
      journeyName: 'Pre-arrival check-in',
      clientId: contract.clientId,
      clientName: contract.clientName,
      clientEmail: contract.client.email,
      clientPhone: contract.client.phone,
      propertyAddress: contract.propertyAddress,
      checkInDate: contract.moveDate,
      checkOutDate: contract.endDate,
      numberOfGuests: contract.numberOfGuests,
      language: ['en', 'de', 'en', 'it'][index % 4],
      status: 'SUBMITTED',
      read: index > 2,
      submittedAt: `${contract.moveDate} 0${8 + (index % 2)}:3${index % 6}`,
      orderId: products.length ? `demo-order-${index}` : null,
      totalUpsellValue: products.reduce((sum, p) => sum + p.price, 0),
      currency: 'EUR',
      answers: [
        {questionKey: 'arrival_time', questionId: 'q1', type: 'TIME', label: 'When will you arrive?', time: `${14 + (index % 7)}:00`, text: `${14 + (index % 7)}:00`},
        {questionKey: 'guests_confirm', questionId: 'q2', type: 'NUMBER', label: 'How many guests?', number: contract.numberOfGuests},
        {questionKey: 'identity', questionId: 'q3', type: 'IDENTITY_VERIFICATION', label: 'Identity verification', booleanValue: contract._demo.verified},
        {questionKey: 'extras', questionId: 'q4', type: 'PRODUCT_PICKER', label: 'Anything for your stay?', products},
        {questionKey: 'notes', questionId: 'q5', type: 'LONG_TEXT', label: 'Anything else?', text: index % 4 === 1 ? 'We are celebrating an anniversary 🎉' : null},
      ],
      definition: [
        {questionKey: 'arrival_time', label: 'When will you arrive?', type: 'TIME', stepTitle: 'Your arrival'},
        {questionKey: 'guests_confirm', label: 'How many guests?', type: 'NUMBER', stepTitle: 'Your arrival'},
        {questionKey: 'identity', label: 'Identity verification', type: 'IDENTITY_VERIFICATION', stepTitle: 'Verification'},
        {questionKey: 'extras', label: 'Anything for your stay?', type: 'PRODUCT_PICKER', stepTitle: 'Extras'},
        {questionKey: 'notes', label: 'Anything else?', type: 'LONG_TEXT', stepTitle: 'Extras'},
      ],
    }
  })
  const from = (page - 1) * amount
  return {Responses: responses.slice(from, from + amount), Total: responses.length, Page: page, Amount: amount}
}
